import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, createVNode as _createVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, Fragment as _Fragment, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = ["aria-busy"]
const _hoisted_2 = /*#__PURE__*/_createElementVNode("p", { class: "text-gray-500 mb-4" }, "Como você quer preencher a vaga?", -1)
const _hoisted_3 = ["aria-disabled"]
const _hoisted_4 = {
  key: 0,
  class: "text-sm text-[#68688D] mt-3",
  "data-testid": "main-doctor-orphan-notice"
}
const _hoisted_5 = { key: 0 }
const _hoisted_6 = /*#__PURE__*/_createElementVNode("p", { class: "text-gray-500 mb-4" }, "O que acontece com o médico atual?", -1)
const _hoisted_7 = {
  class: "text-sm text-[#68688D] mt-2",
  "data-testid": "invitees-hint"
}
const _hoisted_8 = {
  key: 1,
  class: "text-sm bg-warning/20 border border-warning rounded-md px-4 py-2",
  "data-testid": "direct-allocation-warning"
}
const _hoisted_9 = /*#__PURE__*/_createElementVNode("span", { class: "font-semibold" }, "A alocação sem convite será desativada em breve.", -1)

import { computed, watch } from 'vue'
import SelectableCard from '@/components/designSystem/Cards/SelectableCard.vue'
import type {
  FillMode,
  ReplacementChoice
} from '@/modules/company/Dashboard/modals/managementDoctor/inviteWizard'

/** Mesmo item do autocomplete de médico do passo 1 (`userId` null = sem cadastro no app). */
export interface InviteDoctorOption {
  title: string
  value: string
  subtitle: string
  userId: number | null
}

const ORPHAN_TEXT = 'Este médico ainda não tem cadastro no app. Para ele, use a alocação direta.'


export default /*#__PURE__*/_defineComponent({
  __name: 'InviteFillModeStep',
  props: {
    fillMode: {},
    invitees: {},
    replacement: {},
    doctors: {},
    mainDoctorId: {},
    hasCurrentDoctor: { type: Boolean },
    maxInvitees: {},
    disabled: { type: Boolean }
  },
  emits: ["update:fillMode", "update:invitees", "update:replacement"],
  setup(__props: any, { emit }) {

const props = __props;





const mainDoctorIsOrphan = computed(() => {
  const main = props.doctors.find((d) => d.value === props.mainDoctorId)
  return !!main && main.userId === null
})

// D58: órfão não recebe convite — a alocação direta vira o único caminho.
watch(
  mainDoctorIsOrphan,
  (orphan) => {
    if (orphan && props.fillMode !== 'DIRECT') emit('update:fillMode', 'DIRECT')
  },
  { immediate: true }
)

// Quem virou o médico principal sai da lista de convidados (senão iria duplicado no doctorIds).
watch(
  () => props.mainDoctorId,
  (id) => {
    if (id && props.invitees.includes(id)) {
      emit(
        'update:invitees',
        props.invitees.filter((v) => v !== id)
      )
    }
  },
  { immediate: true }
)

function selectFillMode(mode: FillMode) {
  if (props.disabled) return
  if (mode === 'INVITE' && mainDoctorIsOrphan.value) return
  emit('update:fillMode', mode)
}

function selectReplacement(choice: ReplacementChoice) {
  if (props.disabled) return
  emit('update:replacement', choice)
}

// Lista de convidados: sem o médico principal; órfão aparece desabilitado com o motivo (D58).
// O backend limita `doctorIds` (principal + convidados) a `maxInvitees`: no teto, só as
// opções já escolhidas seguem habilitadas (para poder desmarcar).
const inviteeOptions = computed(() => {
  const atLimit = props.invitees.length >= props.maxInvitees - 1
  return props.doctors
    .filter((d) => d.value !== props.mainDoctorId)
    .map((d) => ({
      title: d.title,
      value: d.value,
      props: {
        disabled: d.userId === null || (atLimit && !props.invitees.includes(d.value)),
        subtitle: d.userId === null ? ORPHAN_TEXT : d.subtitle
      }
    }))
})

return (_ctx: any,_cache: any) => {
  const _component_v_autocomplete = _resolveComponent("v-autocomplete")!

  return (_openBlock(), _createElementBlock("div", {
    class: "grid grid-cols-1 gap-6 text-left",
    "aria-busy": _ctx.disabled ? 'true' : undefined,
    "data-testid": "invite-fill-mode-step"
  }, [
    _createElementVNode("div", null, [
      _hoisted_2,
      _createElementVNode("div", {
        class: _normalizeClass(["grid grid-cols-1 md:grid-cols-2 gap-6", { 'pointer-events-none opacity-60': _ctx.disabled }])
      }, [
        _createElementVNode("div", {
          "data-testid": "fill-mode-invite-card",
          "aria-disabled": mainDoctorIsOrphan.value ? 'true' : undefined,
          class: _normalizeClass(mainDoctorIsOrphan.value ? 'opacity-60 cursor-not-allowed' : ''),
          onClick: _cache[0] || (_cache[0] = ($event: any) => (selectFillMode('INVITE')))
        }, [
          _createVNode(SelectableCard, {
            title: "Enviar convite",
            description: "O médico recebe o convite. Ele ocupa a vaga quando aceitar o Termo de Ciência.",
            selected: _ctx.fillMode === 'INVITE',
            class: _normalizeClass(["!flex", { 'pointer-events-none': mainDoctorIsOrphan.value }])
          }, null, 8, ["selected", "class"])
        ], 10, _hoisted_3),
        _createElementVNode("div", {
          "data-testid": "fill-mode-direct-card",
          onClick: _cache[1] || (_cache[1] = ($event: any) => (selectFillMode('DIRECT')))
        }, [
          _createVNode(SelectableCard, {
            title: "Alocar sem convite",
            description: "O médico ocupa a vaga agora. Ele só recebe um aviso.",
            selected: _ctx.fillMode === 'DIRECT',
            class: "!flex"
          }, null, 8, ["selected"])
        ])
      ], 2),
      (mainDoctorIsOrphan.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_4, _toDisplayString(ORPHAN_TEXT)))
        : _createCommentVNode("", true)
    ]),
    (_ctx.fillMode === 'INVITE')
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
          (_ctx.hasCurrentDoctor)
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                _hoisted_6,
                _createElementVNode("div", {
                  class: _normalizeClass(["grid grid-cols-1 md:grid-cols-2 gap-6", { 'pointer-events-none opacity-60': _ctx.disabled }])
                }, [
                  _createElementVNode("div", {
                    "data-testid": "replacement-keep-card",
                    onClick: _cache[2] || (_cache[2] = ($event: any) => (selectReplacement('KEEP')))
                  }, [
                    _createVNode(SelectableCard, {
                      title: "Permanece até o aceite",
                      description: "O médico atual continua no plantão. Ele sai quando um convidado aceitar.",
                      selected: _ctx.replacement === 'KEEP',
                      class: "!flex"
                    }, null, 8, ["selected"])
                  ]),
                  _createElementVNode("div", {
                    "data-testid": "replacement-now-card",
                    onClick: _cache[3] || (_cache[3] = ($event: any) => (selectReplacement('NOW')))
                  }, [
                    _createVNode(SelectableCard, {
                      title: "Sai agora",
                      description: "A vaga fica aberta já. O médico atual recebe um aviso.",
                      selected: _ctx.replacement === 'NOW',
                      class: "!flex"
                    }, null, 8, ["selected"])
                  ])
                ], 2)
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", null, [
            _createVNode(_component_v_autocomplete, {
              "model-value": _ctx.invitees,
              items: inviteeOptions.value,
              "item-title": "title",
              "item-value": "value",
              "item-props": "props",
              label: "Convidar outros médicos (opcional)",
              variant: "outlined",
              color: "primary20",
              density: "comfortable",
              multiple: "",
              chips: "",
              "closable-chips": "",
              clearable: "",
              "hide-details": "",
              "no-data-text": "Nenhum médico encontrado",
              disabled: _ctx.disabled,
              "data-testid": "invitees-select",
              "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => (emit('update:invitees', $event)))
            }, null, 8, ["model-value", "items", "disabled"]),
            _createElementVNode("p", _hoisted_7, " Até " + _toDisplayString(_ctx.maxInvitees) + " médicos por convite, contando o escolhido. Quem aceitar primeiro fica com todos os plantões. ", 1)
          ])
        ], 64))
      : (_openBlock(), _createElementBlock("p", _hoisted_8, [
          _hoisted_9,
          _createTextVNode(" Prefira enviar o convite. Assim o médico confirma que sabe do plantão. ")
        ]))
  ], 8, _hoisted_1))
}
}

})